console.log("===============================");
console.log("=== SERVER IS BOOTING UP... ===");
console.log("===============================");

import './config/env'; // validate env first
import express from 'express';
import http from 'http';
import { Server as SocketIOServer } from 'socket.io';
import helmet from 'helmet';
import cors from 'cors';
import compression from 'compression';
import morgan from 'morgan';
import rateLimit from 'express-rate-limit';

import { env } from './config/env';
import { prisma } from './shared/prisma';
import { redis } from './shared/redis';
import { errorHandler } from './shared/errors/AppError';
import { registerSocketHandlers } from './realtime/socket.bootstrap';

// Routers
import authRouter from './modules/auth/auth.router';
import usersRouter from './modules/users/users.router';
import teamsRouter from './modules/teams/teams.router';
import channelsRouter from './modules/channels/channels.router';
import messagesRouter from './modules/messages/messages.router';
import conversationsRouter from './modules/conversations/conversations.router';
import notificationsRouter from './modules/notifications/notifications.router';
import adminRouter from './modules/admin/admin.router';
import settingsRouter from './modules/settings/settings.router';
import uploadsRouter from './modules/uploads/uploads.router';
import path from 'path';
import fs from 'fs';

const app = express();
const httpServer = http.createServer(app);


// ─── Socket.IO ────────────────────────────────
const io = new SocketIOServer(httpServer, {
  cors: { origin: (origin, callback) => callback(null, origin || true), credentials: true },
  pingTimeout: 60000,
  pingInterval: 25000,
  maxHttpBufferSize: 5e8 // 500MB limit for file attachments via websocket
});
registerSocketHandlers(io);

// ─── Middleware ───────────────────────────────
app.use(helmet({ crossOriginResourcePolicy: { policy: "cross-origin" } })); // Allow cross-origin images
app.use(cors({ origin: (origin, callback) => callback(null, origin || true), credentials: true }));
app.use(compression());
app.use(express.json({ limit: '500mb' }));
app.use(express.urlencoded({ extended: true }));
app.use(morgan(env.NODE_ENV === 'development' ? 'dev' : 'combined'));

// Serve uploads statically
app.use('/uploads', express.static(path.join(process.cwd(), 'uploads')));

// Rate limiting
const limiter = rateLimit({ windowMs: 15 * 60 * 1000, max: 300, standardHeaders: true, legacyHeaders: false });
app.use('/api', limiter);

const authLimiter = rateLimit({ windowMs: 15 * 60 * 1000, max: 1000, message: { success: false, message: 'Too many auth attempts' } });
app.use('/api/auth', authLimiter);

// ─── Routes ───────────────────────────────────
app.use('/api/auth', authRouter);
app.use('/api/users', usersRouter);
app.use('/api/teams', teamsRouter);
app.use('/api/teams/:teamId/channels', channelsRouter);
app.use('/api/channels', channelsRouter);
app.use('/api/messages', messagesRouter);
app.use('/api/conversations', conversationsRouter);
app.use('/api/notifications', notificationsRouter);
app.use('/api/admin', adminRouter);
app.use('/api/settings', settingsRouter);
app.use('/api/upload', uploadsRouter);

// Health check
app.get('/health', (_req, res) => res.json({ status: 'ok', timestamp: new Date().toISOString() }));

// 404
app.use((_req, res) => res.status(404).json({ success: false, message: 'Not found' }));

// Error handler (must be last)
app.use(errorHandler);

// ─── File Cleanup Job (15 Days) ───────────────
setInterval(async () => {
  try {
    const fifteenDaysAgo = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000);
    const oldAttachments = await prisma.attachment.findMany({
      where: { createdAt: { lt: fifteenDaysAgo } }
    });
    
    for (const att of oldAttachments) {
      const filePath = path.join(process.cwd(), att.storageUrl);
      if (fs.existsSync(filePath)) {
        fs.unlinkSync(filePath);
      }
    }
    
    await prisma.attachment.deleteMany({
      where: { createdAt: { lt: fifteenDaysAgo } }
    });
    if (oldAttachments.length > 0) {
      console.log(`[Cleanup] Deleted ${oldAttachments.length} old attachments.`);
    }
  } catch (err) {
    console.error('[Cleanup] Error cleaning up old attachments', err);
  }
}, 24 * 60 * 60 * 1000); // Run once a day

// ─── Startup ──────────────────────────────────
async function bootstrap() {
  try {
    await prisma.$connect();
    console.log('✅ PostgreSQL connected');

    await redis.connect();

    const port = Number(env.PORT);
    httpServer.listen(port, () => {
      console.log(`\n🚀 iChat Server running on http://localhost:${port}`);
      console.log(`📡 Socket.IO ready`);
      console.log(`🌍 Environment: ${env.NODE_ENV}\n`);
    });
  } catch (err) {
    console.error('❌ Failed to start server:', err);
    process.exit(1);
  }
}

bootstrap();

// Graceful shutdown
process.on('SIGTERM', async () => {
  console.log('SIGTERM received, shutting down gracefully...');
  await prisma.$disconnect();
  await redis.quit();
  process.exit(0);
});
